// Gantt Chart — data access.
//
// The FSD puts "the upstream data feed that populates the chart's data source"
// out of scope, and the chart needs exactly the hierarchy the Product
// Development dashboard already serves: project -> campaign -> stage -> batch
// with batch dates and statuses. So the portfolio is read from the dashboard's
// own /fetchProjectDashboard (fetchProjectsAPI) rather than through a new
// endpoint, and the CMO / Site selectors reuse the same option endpoints the
// dashboard filter bar uses.
//
// Nothing here is stubbed; a failure resolves to an empty result of the same
// shape so the screen shows its own empty state instead of throwing.

import { fetchProjectsAPI } from '../Dashboard/dashboardAPI';
import { fetchCMOMembersAPI, fetchMfgPlantsAPI } from '../Dashboard/filtersAPI';

// The chart is a portfolio-wide view, so it must not inherit the dashboard's
// 10-row page. A large page size fetches the whole portfolio in one request —
// the same approach the Document Repository popup takes when it needs the full
// project/campaign tree rather than a page of it.
const PORTFOLIO_PAGE_SIZE = 500;

/**
 * The whole portfolio, optionally scoped to a CMO and/or site.
 * @returns {Promise<{status: boolean, projects: Array}>}
 */
export const fetchGanttPortfolioAPI = async ({ cmoOrgId, cmoSite } = {}) => {
  try {
    const res = await fetchProjectsAPI({
      cmoOrgId: cmoOrgId || 'all',
      cmoSite: cmoSite || 'all',
      pageSize: PORTFOLIO_PAGE_SIZE,
      pageNumber: 1,
    });
    const projects = (res && res.projects) || [];
    return { status: !!(res && res.status), projects };
  } catch (error) {
    console.error('[GanttChart] Failed to load portfolio:', error);
    return { status: false, projects: [] };
  }
};

/**
 * CMO options for the facility selector (FSD A). Shape: [{ id, label }].
 *
 * The FSD's single hard-coded CMO ('Sai Life Sciences') is one customer's
 * value; the real list is the ecosystem's CMO members, so a sponsor with
 * several CDMOs sees all of them and the default is simply the first. Field
 * names and de-duplication match the dashboard's own filter bar
 * (FilterSection's cmoOptions): a member is { organization_id, organization_name }.
 */
export const fetchGanttCmoOptionsAPI = async () => {
  try {
    const res = await fetchCMOMembersAPI();
    const list = (res && res.data) || [];
    const seen = new Set();
    const options = [];
    list.forEach((m) => {
      if (!m || m.organization_id == null) return;
      const id = String(m.organization_id);
      if (seen.has(id)) return;
      seen.add(id);
      options.push({ id, label: m.organization_name || id });
    });
    return { status: true, data: options };
  } catch (error) {
    console.error('[GanttChart] Failed to load CMO options:', error);
    return { status: false, data: [] };
  }
};

/**
 * Site / Plant options for the facility selector (FSD A), narrowed to the
 * selected CMO. Shape: [{ id, label }] where `id` is the SITE NAME — that is
 * what /fetchProjectDashboard filters on (its `cmoSite` payload field).
 *
 * Resolution copied from the dashboard's own siteOptions so the two filters
 * always offer the same list: the name is SiteName (cmo_sites.siteShortName)
 * falling back to mfgPlantShortName for a plant not yet linked to a site, and
 * the label pairs the two as "<Site> / <Plant>" — which is exactly the form the
 * FSD quotes ("HYD / Unit II", "Bidar / Unit IV").
 */
export const fetchGanttSiteOptionsAPI = async (cmoOrgId) => {
  try {
    const res = await fetchMfgPlantsAPI();
    const list = (res && res.data) || [];
    const selectedOrgId = cmoOrgId != null && cmoOrgId !== '' && cmoOrgId !== 'all'
      ? String(cmoOrgId)
      : null;
    const siteNameOf = (p) => {
      const site = p && p.SiteName != null ? String(p.SiteName).trim() : '';
      if (site) return site;
      return p && p.mfgPlantShortName != null ? String(p.mfgPlantShortName).trim() : '';
    };
    const seen = new Set();
    const options = [];
    list.forEach((p) => {
      const name = siteNameOf(p);
      if (!name) return;
      if (selectedOrgId != null && String(p.vendorMosymphonyID) !== selectedOrgId) return;
      if (seen.has(name)) return;
      seen.add(name);
      const plant = p && p.mfgPlantShortName != null ? String(p.mfgPlantShortName).trim() : '';
      options.push({ id: name, label: plant && plant !== name ? `${name} / ${plant}` : name });
    });
    return { status: true, data: options };
  } catch (error) {
    console.error('[GanttChart] Failed to load site options:', error);
    return { status: false, data: [] };
  }
};
